/**
 * Adapted from https://github.com/joe-bell/cva/blob/main/packages/cva/src/index.ts
 */
import { clsx } from 'clsx';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type AtLeastOne<
  T,
  U = { [K in keyof Required<T>]: Pick<Required<T>, K> },
> = Partial<T> & U[keyof U];
/* Types
  ============================================ */

// When compiling with `declaration: true`, many projects experience the dreaded
// TS2742 error. To combat this, we copy clsx's types manually.
// Should this project move to JSDoc, this workaround would no longer be needed.

/**
 * Any value accepted by {@link cx} as a class-input slot. Mirrors clsx's
 * input union: strings, numbers, falsy values, dictionaries (truthy keys
 * → class names), and arrays (recursively flattened).
 */
export type ClassValue =
  | ClassArray
  | ClassDictionary
  | string
  | number
  | bigint
  | null
  | boolean
  | undefined;
/** Object form for {@link ClassValue}: truthy entries contribute their key. */
export type ClassDictionary = Record<string, any>;
/** Array form for {@link ClassValue}: flattened recursively by {@link cx}. */
export type ClassArray = ClassValue[];

type ResponsiveValue<T> = T | AtLeastOne<{ [key in Breakpoint]?: T }>;

/* Utils
  ---------------------------------- */

type OmitUndefined<T> = T extends undefined ? never : T;
type StringToBoolean<T> = T extends 'true' | 'false' ? boolean : T;
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * Extracts the variant prop type from a {@link cva} return so component
 * props can extend the variant surface without restating every key.
 *
 * @example
 * ```ts
 * const button = cva('rounded', {
 *   variants: { intent: { primary: 'bg-blue', secondary: 'bg-gray' } },
 * });
 * type ButtonProps = VariantProps<typeof button>; // { intent?: 'primary' | 'secondary' }
 * ```
 */
export type VariantProps<Component extends (...args: any) => any> = Omit<
  OmitUndefined<Parameters<Component>[0]>,
  'class' | 'className'
>;

/* compose
  ---------------------------------- */

/**
 * Type of the {@link compose} helper — merges multiple cva-generated
 * functions into one that accepts the union of their variant props and
 * concatenates the results. Useful when one component needs to compose
 * styles from several variant systems (e.g. layout + color + density).
 */
export interface Compose {
  <T extends ReturnType<CVA>[]>(
    ...components: [...T]
  ): (
    props?: (
      | UnionToIntersection<
          {
            [K in keyof T]: VariantProps<T[K]>;
          }[number]
        >
      | undefined
    ) &
      CVAClassProp,
  ) => string;
}

/* cx
  ---------------------------------- */

/**
 * Type of the {@link cx} helper — variadic class-name concatenation.
 * Mirrors clsx's API but routed through {@link DefineConfigOptions.hooks.onComplete}
 * when a configured instance opts in (e.g. for Tailwind merge).
 */
export interface CX {
  (...inputs: ClassValue[]): string;
}

/** Tuple of arguments accepted by {@link cx}. */
export type CXOptions = Parameters<CX>;
/** Return type of {@link cx} (always `string`). */
export type CXReturn = ReturnType<CX>;

/* cva
  ============================================ */

type CVAConfigBase = { base?: ClassValue };

type CVAVariantShape = Record<string, Record<string, ClassValue>>;

type CVAVariantSchema<V extends CVAVariantShape, R extends CVAVariantShape> = {
  [Variant in keyof V]?: StringToBoolean<keyof V[Variant]> | undefined;
} & {
  [Variant in keyof R]?: ResponsiveValue<StringToBoolean<keyof R[Variant]>>;
};

type CVAClassProp =
  | {
      class?: ClassValue;
      className?: never;
    }
  | {
      class?: never;
      className?: ClassValue;
    };

/**
 * Type of the {@link cva} helper — builds a class-variance-authority
 * function from a config of `base`, `variants`, `responsive` variants,
 * `compoundVariants`, and `defaultVariants`. The returned function
 * accepts a prop bag selecting one value per variant plus optional
 * `class` / `className` overrides and returns a single class string.
 *
 * @example
 * ```ts
 * const button = cva({ base: 'rounded', variants: { size: { sm: 'text-sm' } } });
 * button({ size: 'sm' });
 * ```
 */
export interface CVA {
  <
    _ extends "cva's generic parameters are restricted to internal use only.",
    V,
    R extends CVAVariantShape = CVAVariantShape,
  >(
    config: V extends CVAVariantShape
      ? R extends CVAVariantShape
        ? CVAConfigBase & {
            variants?: V;
            responsive?: R;
            compoundVariants?: (V extends CVAVariantShape
              ? (
                  | CVAVariantSchema<V, R>
                  | ({
                      [Variant in keyof V]?:
                        | StringToBoolean<keyof V[Variant]>
                        | StringToBoolean<keyof V[Variant]>[]
                        | undefined;
                    } & {
                      [Variant in keyof R]?:
                        | StringToBoolean<keyof R[Variant]>
                        | StringToBoolean<keyof R[Variant]>[]
                        | undefined;
                    })
                ) &
                  CVAClassProp
              : CVAClassProp)[];
            defaultVariants?: CVAVariantSchema<V, R>;
          }
        : CVAConfigBase & {
            variants?: V;
            responsive?: R;
            compoundVariants?: (V extends CVAVariantShape
              ? (
                  | CVAVariantSchema<V, R>
                  | {
                      [Variant in keyof V]?:
                        | StringToBoolean<keyof V[Variant]>
                        | StringToBoolean<keyof V[Variant]>[]
                        | undefined;
                    }
                ) &
                  CVAClassProp
              : CVAClassProp)[];
            defaultVariants?: CVAVariantSchema<V, R>;
          }
      : CVAConfigBase & {
          variants?: never;
          responsive?: never;
          compoundVariants?: never;
          defaultVariants?: never;
        },
  ): (
    props?: V extends CVAVariantShape
      ? CVAVariantSchema<V, R> & CVAClassProp
      : CVAClassProp,
  ) => string;
}

/* defineConfig
  ---------------------------------- */

/**
 * Hooks accepted by {@link defineConfig}. Currently exposes an
 * `onComplete` hook that runs on every concatenated class string, used
 * here to plug in tailwind-merge for de-duplicated final output.
 */
export interface DefineConfigOptions {
  hooks?: {
    /**
     * Returns the completed string of concatenated classes/classNames.
     */
    onComplete?: (className: string) => string;
  };
}

/**
 * Factory for a configured cva instance. Consumers can swap in their
 * own `onComplete` hook (e.g. `twMerge`) and use the returned
 * `{ compose, cx, cva }` triplet just like the default exports of this
 * module.
 *
 * @example
 * ```ts
 * const { cva, cx, compose } = defineConfig({
 *   hooks: { onComplete: (cls) => twMerge(cls) },
 * });
 * ```
 */
export interface DefineConfig {
  (options?: DefineConfigOptions): {
    compose: Compose;
    cx: CX;
    cva: CVA;
  };
}

/* Exports
  ============================================ */

const falsyToString = <T>(value: T) =>
  typeof value === 'boolean' ? `${value}` : value === 0 ? '0' : value;

/**
 * Creates a cva instance. Pass `hooks.onComplete` to post-process every
 * class string the instance emits — typically used to deduplicate
 * Tailwind utilities via `twMerge`.
 *
 * Returns `{ cva, cx, compose }` — use these to build variant-aware
 * components without depending on the default singleton.
 */
export const defineConfig: DefineConfig = (options) => {
  const cx: CX = (...inputs) => {
    if (typeof options?.hooks?.onComplete !== 'undefined')
      return options?.hooks.onComplete(clsx(inputs));

    return clsx(inputs);
  };

  const cva: CVA = (config) => (props) => {
    if (config?.variants == null && config?.responsive == null)
      return cx(config?.base, props?.class, props?.className);

    const { variants, responsive, defaultVariants } = config;

    // Handle regular variants
    const getVariantClassNames = variants
      ? Object.keys(variants).map((variant: keyof typeof variants) => {
          const variantProp = props?.[variant as keyof typeof props];
          const defaultVariantProp = defaultVariants?.[variant];

          const variantKey = (falsyToString(variantProp) ||
            falsyToString(
              defaultVariantProp,
            )) as keyof (typeof variants)[typeof variant];

          return variants[variant][variantKey];
        })
      : [];

    // Handle responsive variants
    const getResponsiveClassNames = responsive
      ? Object.keys(responsive).map((variant: keyof typeof responsive) => {
          const variantProp = props?.[variant as keyof typeof props];
          const defaultVariantProp =
            defaultVariants?.[variant as keyof typeof defaultVariants];

          // Handle responsive props
          if (
            typeof variantProp === 'object' &&
            variantProp !== null &&
            !Array.isArray(variantProp)
          ) {
            // Generate responsive classes dynamically
            const responsiveClasses = Object.entries(
              variantProp as Record<string, unknown>,
            ).map(([breakpoint, value]) => {
              const variantKey = falsyToString(
                value,
              ) as keyof (typeof responsive)[typeof variant];
              if (breakpoint === 'xs') {
                return responsive[variant][variantKey];
              }
              return `${breakpoint}:${responsive[variant][variantKey]}`;
            });
            return clsx(responsiveClasses);
          }

          // Handle regular props (fallback to default)
          const variantKey = (falsyToString(variantProp) ||
            falsyToString(
              defaultVariantProp,
            )) as keyof (typeof responsive)[typeof variant];

          return responsive[variant][variantKey];
        })
      : [];

    const defaultsAndProps = {
      ...defaultVariants,
      // remove `undefined` props
      ...(props &&
        Object.entries(props).reduce<typeof props>(
          (acc, [key, value]) =>
            typeof value === 'undefined' ? acc : { ...acc, [key]: value },
          {} as typeof props,
        )),
    };

    const getCompoundVariantClassNames = config?.compoundVariants?.reduce(
      (acc, { class: cvClass, className: cvClassName, ...cvConfig }) =>
        Object.entries(cvConfig).every(([cvKey, cvSelector]) => {
          const selector =
            defaultsAndProps[cvKey as keyof typeof defaultsAndProps];

          return Array.isArray(cvSelector)
            ? cvSelector.includes(selector)
            : selector === cvSelector;
        })
          ? [...acc, cvClass, cvClassName]
          : acc,
      [] as ClassValue[],
    );

    return cx(
      config?.base,
      getVariantClassNames,
      getCompoundVariantClassNames,
      getResponsiveClassNames,
      props?.class,
      props?.className,
    );
  };

  const compose: Compose =
    (...components) =>
    (props) => {
      const propsWithoutClass = Object.fromEntries(
        Object.entries(props || {}).filter(
          ([key]) => !['class', 'className'].includes(key),
        ),
      );

      return cx(
        components.map((component) => component(propsWithoutClass)),
        props?.class,
        props?.className,
      );
    };

  return {
    compose,
    cva,
    cx,
  };
};

/**
 * Default cva instance — built from {@link defineConfig} with no hooks.
 *
 * - `cva(base, config?)` builds a variant-aware class-name function.
 *   See {@link CVA} for the full config shape and an example.
 * - `cx(...inputs)` concatenates {@link ClassValue} inputs into a
 *   single class string (clsx semantics).
 * - `compose(...cvaResults)` merges multiple {@link cva} returns into
 *   one function whose prop type is the union of their variant props.
 *
 * For Tailwind users who want `twMerge`-style deduplication, build a
 * private instance with {@link defineConfig} instead.
 */
export const { compose, cva, cx } = defineConfig();
